import { ButtonBase } from '@mui/material'
import type { CSSProperties, FC } from 'react'
import { LikeHeart } from 'src/components/LikeButton/LikeHeart'
import { executeMarkArtwork } from 'src/services/apollo/artwork'
import { formatNum } from 'src/utils/formatNum'
import { track } from 'src/utils/tracker'
import type {
  Artwork,
  TackPreviewFragment,
} from '../../services/apollo/generated/graphql'
import { MarkType, RefType } from '../../services/apollo/generated/graphql'
import { cx } from '../../utils/cx'
import { getOpenArtworkDetailQuery } from '../ArtworkDetail/utils'
import BaseLikeButton from '../LikeButton/BaseLikeButton'

const ArtworkLikeButton: FC<{
  artworkId: string
  artwork?: Pick<Artwork, 'liked' | 'likedCount' | 'authorId'> & {
    tacks?: TackPreviewFragment[] | null
  }
  initialValue?: boolean
  initialCount?: number
  countStyle?: CSSProperties
  numOnTop?: boolean
  hideValueOnZero?: boolean

  afterClick?: () => void
  trackingArgs?: Record<string, any>

  /**
   * Append artworkId and openDetail to query string
   * Open back artwork after oauth completes
   * @returns
   */
  oauthReturnOpensDetail?: boolean
}> = ({
  artworkId,
  artwork,
  initialValue = artwork?.liked ?? false,
  initialCount = artwork?.likedCount ?? 0,
  countStyle,
  numOnTop,
  hideValueOnZero,
  trackingArgs,

  afterClick,
  oauthReturnOpensDetail,
}) => {
  return (
    <BaseLikeButton
      id={artworkId}
      initialCount={initialCount}
      initialValue={initialValue}
      onBtnClick={target => {
        if (target) {
          track(
            'like_click',
            {
              id: artworkId,
              tags: artwork?.tacks?.map(({ codeName }) => codeName),
              tackIds: artwork?.tacks?.map(({ id }) => id),
              tackCodeNames: artwork?.tacks?.map(({ codeName }) => codeName),
              tackDefaultNames: artwork?.tacks?.map(
                ({ defaultName }) => defaultName,
              ),
              authorId: artwork?.authorId,
              type: RefType.Artwork,
              ...trackingArgs,
            },
            // Skip getway
            { destinations: ['amplitude'] },
          )
          afterClick?.()
        }
      }}
      handler={(id, target) =>
        executeMarkArtwork({
          artworkId: id,
          type: MarkType.Like,
          target,
        })
      }
      render={({ value, count, onClick }) => {
        return (
          <ButtonBase
            component="div"
            role="button"
            className="cursor-pointer outline transition -m-2 p-2 rounded-md focus-within:outline-1"
            onClick={onClick}
          >
            <div
              className={cx('flex gap-1 items-center', {
                'flex-col-reverse': numOnTop,
              })}
            >
              <LikeHeart liked={value} className="size-5" />
              {hideValueOnZero && !count ? null : (
                <span
                  className="text-sm tabular-nums"
                  style={{
                    color: 'inherit',
                    ...countStyle,
                  }}
                >
                  {formatNum(count || 0)}
                </span>
              )}
            </div>
          </ButtonBase>
        )
      }}
      signModalProps={
        oauthReturnOpensDetail && typeof location !== 'undefined'
          ? {
              oauthRedirectUrl: getOpenArtworkDetailQuery(
                location.href,
                artworkId,
              ),
            }
          : undefined
      }
    />
  )
}

export default ArtworkLikeButton
